import { type Format, type MatchupCell } from '@ugin/shared';
import { Crosshair } from 'lucide-react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { type WidgetComponent, type WidgetMeta } from '../_base/types';
import { WidgetContainer } from '../_base/WidgetContainer';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useMatchups } from '@/hooks/useMeta';
import { cn, formatPercent } from '@/lib/utils';


const meta: WidgetMeta = {
  id: 'matchup-heatmap',
  name: 'Matchup heatmap',
  description: 'Your decks against what shows up at the table.',
  icon: Crosshair,
  defaultSize: { w: 8, h: 5 },
  category: 'performance',
};

export interface MatchupHeatmapProps {
  format?: Format;
  deckId?: string;
}

export const MatchupHeatmapWidget: WidgetComponent<MatchupHeatmapProps> = ({ format, deckId }) => {
  const navigate = useNavigate();
  const params = useMemo(
    () => ({ ...(format && { format }), ...(deckId && { deckId }) }),
    [format, deckId],
  );
  const { data, isLoading, error, refetch } = useMatchups(params);

  const grid = useMemo(() => buildGrid(data?.cells ?? []), [data?.cells]);

  return (
    <WidgetContainer
      meta={meta}
      icon={<Crosshair className="size-4" />}
      loading={isLoading}
      error={error ?? undefined}
      onRetry={() => void refetch()}
      empty={!isLoading && grid.cells.length === 0}
      emptyTitle="No matches recorded yet"
      emptyDescription={
        <>
          Once Detailed Logs is enabled in Arena and you play a few games, Ugin will fill in the
          matchup matrix. Sparse data is fine — he'll mark it as such.
        </>
      }
    >
      {grid.cells.length > 0 ? (
        <TooltipProvider delayDuration={120}>
          <div className="overflow-x-auto">
            <table className="border-separate border-spacing-1 text-xs">
              <thead>
                <tr>
                  <th aria-hidden="true" />
                  {grid.archetypes.map((arch) => (
                    <th
                      key={arch}
                      className="rotate-[-35deg] origin-bottom-left text-left text-ugin-text-muted pl-2 pb-2 whitespace-nowrap"
                    >
                      {arch}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {grid.deckIds.map((id) => (
                  <tr key={id}>
                    <td className="pr-2 text-right text-ugin-text-muted whitespace-nowrap">
                      {grid.deckNames.get(id) ?? id}
                    </td>
                    {grid.archetypes.map((arch) => {
                      const cell = grid.byKey.get(`${id}::${arch}`);
                      return (
                        <td key={arch} className="p-0">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                aria-label={
                                  cell
                                    ? `${grid.deckNames.get(id) ?? id} vs ${arch}: ${cell.wins}-${cell.losses}-${cell.draws}${
                                        cell.provisional ? ' (provisional)' : ''
                                      }`
                                    : `${grid.deckNames.get(id) ?? id} vs ${arch}: no data`
                                }
                                onClick={() =>
                                  navigate(
                                    `/chat?prompt=${encodeURIComponent(`Brief me on ${grid.deckNames.get(id) ?? id} vs ${arch}.`)}`,
                                  )
                                }
                                className={cn(
                                  'block h-8 w-8 rounded-sm transition-all hover:scale-105 hover:ring-2 hover:ring-ugin-accent',
                                  cell
                                    ? colorForWr(cell.winRate, cell.wins + cell.losses + cell.draws)
                                    : 'bg-ugin-surface-elevated',
                                  cell?.provisional === true &&
                                    'bg-[image:repeating-linear-gradient(45deg,transparent_0_3px,rgba(0,0,0,0.35)_3px_6px)]',
                                )}
                              />
                            </TooltipTrigger>
                            <TooltipContent>
                              {cell ? (
                                <>
                                  {grid.deckNames.get(id)} vs {arch} —{' '}
                                  <strong>{formatPercent(cell.winRate)}</strong> ({cell.wins}-{cell.losses}
                                  {cell.draws > 0 ? `-${cell.draws}` : ''})
                                  {cell.provisional ? (
                                    <div className="mt-1 text-ugin-warn">
                                      Provisional — {cell.wins + cell.losses + cell.draws} of 20 matches
                                    </div>
                                  ) : null}
                                </>
                              ) : (
                                <>No data — Ugin will know more after you play this matchup.</>
                              )}
                            </TooltipContent>
                          </Tooltip>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-ugin-text-faint">
            Sample size: {data?.sampleSize ?? 0} matches. Click any cell to ask Ugin for the brief.
          </p>
        </TooltipProvider>
      ) : null}
    </WidgetContainer>
  );
};

MatchupHeatmapWidget.meta = meta;

/** Column label for opponents Ugin would not name. */
export const UNCLASSIFIED = 'Unclassified';

interface Grid {
  archetypes: string[];
  deckIds: string[];
  deckNames: Map<string, string>;
  byKey: Map<string, MatchupCell>;
  cells: MatchupCell[];
}

function labelOf(cell: MatchupCell): string {
  return cell.archetype ?? UNCLASSIFIED;
}

function buildGrid(cells: MatchupCell[]): Grid {
  // Unclassified sorts last: it is a gap in the data, not an archetype.
  const named = Array.from(new Set(cells.filter((c) => c.archetype !== null).map(labelOf))).sort();
  const archetypes = cells.some((c) => c.archetype === null) ? [...named, UNCLASSIFIED] : named;
  const deckNames = new Map<string, string>();
  const byKey = new Map<string, MatchupCell>();
  for (const c of cells) {
    deckNames.set(c.deckId, c.deckName);
    byKey.set(`${c.deckId}::${labelOf(c)}`, c);
  }
  const deckIds = Array.from(new Set(cells.map((c) => c.deckId))).sort((a, b) =>
    (deckNames.get(a) ?? '').localeCompare(deckNames.get(b) ?? ''),
  );
  return { archetypes, deckIds, deckNames, byKey, cells };
}

function colorForWr(wr: number, sample: number): string {
  const opacity = Math.min(1, 0.35 + sample / 12);
  if (wr >= 0.55)
    return cn(
      'bg-emerald-500',
      opacity < 0.5 ? 'opacity-50' : opacity < 0.75 ? 'opacity-70' : 'opacity-95',
    );
  if (wr <= 0.45)
    return cn(
      'bg-rose-500',
      opacity < 0.5 ? 'opacity-50' : opacity < 0.75 ? 'opacity-70' : 'opacity-95',
    );
  return cn(
    'bg-amber-400',
    opacity < 0.5 ? 'opacity-40' : opacity < 0.75 ? 'opacity-60' : 'opacity-85',
  );
}
